/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, February 11th 2022, 5:53:22 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { getEmptyImage } from "react-dnd-html5-backend";
import { useDrag } from "react-dnd";
import { Identifier } from "dnd-core";

interface UseGenericDragSourceSpec<GenericDraggedItemState> {
  itemType: Identifier;
  dragItemState: GenericDraggedItemState;
  handleFailedDrop?: (dragItemState: GenericDraggedItemState) => void;
  hideDefaultDragPreview?: boolean;
}

interface GenericCollectedDragSpec {
  itemIsBeingDragged: boolean;
}

export const useGenericDragSource = <GenericDraggedItemState,>({
  itemType,
  dragItemState,
  handleFailedDrop,
  hideDefaultDragPreview = false,
}: UseGenericDragSourceSpec<GenericDraggedItemState>) => {
  const [{ itemIsBeingDragged }, dragSourceRef, dragPreviewRef] = useDrag<
    GenericDraggedItemState,
    any,
    GenericCollectedDragSpec
  >(
    () => ({
      type: itemType,
      item: dragItemState,
      collect: (monitor) => ({
        itemIsBeingDragged: monitor.isDragging(),
      }),
      // Commenting out dropEffect because its unreliable. Interferes with drag at times
      // options: {
      //   dropEffect: "copy",
      // },
      end: (item, monitor) => {
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          handleFailedDrop && handleFailedDrop(item); // Handle drag end in case we need to handle anything that cannot be caught on the drop e.g. a cancellation action.
        }
      },
    }),
    [handleFailedDrop, dragItemState]
  );

  hideDefaultDragPreview && dragPreviewRef(getEmptyImage()); // Feeding the browser an empty image so we can have a custom drag preview via the drag layer component - https://react-dnd.github.io/react-dnd/examples/drag-around/custom-drag-layer

  return { dragSourceRef, itemIsBeingDragged };
};

interface GenericDragSourceProps<GenericDraggedItemState> extends UseGenericDragSourceSpec<GenericDraggedItemState> {
  children: React.ReactNode;
}

export const GenericDragSource = <GenericDraggedItemState,>({
  children,
  itemType,
  dragItemState,
  handleFailedDrop,
  hideDefaultDragPreview,
}: GenericDragSourceProps<GenericDraggedItemState>) => {
  const { dragSourceRef } = useGenericDragSource<GenericDraggedItemState>({
    dragItemState,
    itemType,
    handleFailedDrop,
    hideDefaultDragPreview,
  });

  return <div ref={dragSourceRef}>{children}</div>;
};
